import React from "react";
import {
  Box,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { IconChevronRight, IconFolder, IconPackages } from "@tabler/icons-react";
import {
  cardDoneSx,
  cardSx,
  ds,
  dsText,
  sectionLabelSx,
  softChipSx,
} from "../theme/keeperTheme";
import { countGroupsInCatalogSubtree } from "../utils/assemblyCatalogView";

/**
 * Переключатель «по производителям», хлебные крошки каталога, папки и список товаров.
 * Режим заказов/отправлений рендерит родитель через `children`.
 */
export default function ManufacturerCatalogPanel({
  groupByManufacturer,
  onGroupByManufacturerChange,
  catalogStack,
  onCatalogBreadcrumbClick,
  visibleCatalogs,
  groups,
  catalogsIndex,
  currentCatalogId,
  filteredIncomplete,
  filteredComplete,
  onEnterCatalog,
  linesLoading,
  linesError,
  defaultListEmpty,
  renderProductCard,
}) {

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 0.75,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={sectionLabelSx}>
          {groupByManufacturer ? "КАТАЛОГ / ТОВАРЫ" : "К СБОРКЕ"}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={groupByManufacturer}
              onChange={(e) => onGroupByManufacturerChange(e.target.checked)}
            />
          }
          label="По производителям"
          sx={{
            m: 0,
            "& .MuiFormControlLabel-label": {
              color: ds.text2,
              fontSize: "0.72rem",
              fontWeight: 600,
            },
          }}
        />
      </Box>

      {groupByManufacturer && catalogStack.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.35,
            mb: 0.85,
          }}
        >
          <Typography
            component="button"
            type="button"
            onClick={() => onCatalogBreadcrumbClick(0)}
            sx={{
              color: ds.primary,
              fontSize: "0.6875rem",
              fontWeight: 600,
              cursor: "pointer",
              border: 0,
              background: "none",
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Каталоги
          </Typography>
          {catalogStack.map((item, index) => (
            <React.Fragment key={item.id}>
              <IconChevronRight size={14} color={ds.text3} />
              <Typography
                component="button"
                type="button"
                onClick={() => onCatalogBreadcrumbClick(index + 1)}
                sx={{
                  color: index === catalogStack.length - 1 ? ds.text : ds.text2,
                  fontSize: "0.6875rem",
                  fontWeight: index === catalogStack.length - 1 ? 700 : 500,
                  cursor: "pointer",
                  border: 0,
                  background: "none",
                  p: 0,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {item.name}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}

      {groupByManufacturer ? (
        <>
          {linesLoading && (
            <Typography sx={{ color: ds.text2, fontSize: "0.75rem" }}>
              Загрузка…
            </Typography>
          )}
          {!linesLoading && linesError && (
            <Typography sx={{ color: dsText.danger, fontSize: "0.75rem" }}>
              {linesError}
            </Typography>
          )}
          {!linesLoading &&
            !linesError &&
            visibleCatalogs.length === 0 &&
            filteredIncomplete.length === 0 &&
            filteredComplete.length === 0 && (
              <Typography sx={{ color: ds.text2, fontSize: "0.75rem" }}>
                {currentCatalogId == null
                  ? "Нет товаров на сборке с привязкой к каталогам склада"
                  : "В этом каталоге нет товаров на сборке"}
              </Typography>
            )}

          {!linesLoading && !linesError && visibleCatalogs.length > 0 && (
            <Box
              sx={{
                mb:
                  filteredIncomplete.length || filteredComplete.length ? 1 : 0,
              }}
            >
              {visibleCatalogs.map((catalog) => {
                const count = countGroupsInCatalogSubtree(
                  catalog.id,
                  groups,
                  catalogsIndex,
                );
                return (
                  <Box
                    key={`cat-${catalog.id}`}
                    component="button"
                    type="button"
                    onClick={() => onEnterCatalog(catalog)}
                    sx={{
                      ...cardSx,
                      width: "100%",
                      minHeight: 48,
                      mb: 1,
                      py: 1.1,
                      px: 1.4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      cursor: "pointer",
                      textAlign: "left",
                      color: ds.text,
                      "&:hover": {
                        borderColor: ds.primary,
                        bgcolor: ds.primarySoft,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        borderRadius: `${ds.radius}px`,
                        bgcolor: ds.primarySoft,
                        color: ds.primaryHover,
                      }}
                    >
                      <IconFolder size={20} stroke={1.5} />
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: ds.text,
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                      noWrap
                    >
                      {catalog.name}
                    </Typography>
                    <Chip
                      label={`${count} поз.`}
                      size="small"
                      sx={softChipSx("neutral")}
                    />
                    <IconChevronRight size={18} color={ds.text3} />
                  </Box>
                );
              })}
            </Box>
          )}

          {!linesLoading &&
            !linesError &&
            (filteredIncomplete.length > 0 || filteredComplete.length > 0) && (
              <>
                <Typography sx={{ ...sectionLabelSx, mb: 0.75, display: "block" }}>
                  {currentCatalogId == null ? "БЕЗ КАТАЛОГА" : "ТОВАРЫ В КАТАЛОГЕ"}
                </Typography>
                {filteredIncomplete.map((g) => renderProductCard(g, "incomplete"))}
              </>
            )}

          {filteredComplete.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...sectionLabelSx, mb: 0.75, display: "block" }}>
                СОБРАНО ПОЛНОСТЬЮ
              </Typography>
              {filteredComplete.map((g) => renderProductCard(g, "complete"))}
            </Box>
          )}
        </>
      ) : (
        defaultListEmpty
      )}
    </>
  );
}

/** Карточка товара в режиме по производителям (Ozon / Маркет). */
export function CatalogProductCard({ product, variant = "incomplete" }) {
  const complete = product.need > 0 && product.done >= product.need;
  const showComplete = variant === "complete" || complete;
  const sub =
    product.sku != null
      ? product.offer_id
        ? `SKU ${product.sku} · ${product.offer_id}`
        : `SKU ${product.sku}`
      : product.offer_id
        ? `Арт. ${product.offer_id}`
        : "";

  return (
    <Box
      sx={{
        ...(showComplete ? cardDoneSx : cardSx),
        mb: 1,
        py: 1.1,
        px: 1.4,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <IconPackages size={18} stroke={1.75} color={ds.text3} style={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: ds.text,
            fontSize: "0.8125rem",
            fontWeight: 700,
            lineHeight: 1.3,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {product.title || sub || product.key}
        </Typography>
        {sub && product.title ? (
          <Typography sx={{ color: ds.text3, fontSize: "0.72rem", mt: 0.25 }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
      <Chip
        label={
          product.need === 0
            ? "Без КИЗ"
            : showComplete
              ? "Готово"
              : `${product.done} из ${product.need}`
        }
        size="small"
        sx={softChipSx(showComplete ? "success" : "primary")}
      />
    </Box>
  );
}
